/**
 * Client-side geofence math for the delivery-area preview (mirrors the server's GeoUtils):
 * haversine distance, GeoJSON polygon parsing and point-in-polygon. Used only for instant UX
 * feedback while the pin moves — the server re-checks at quote time and again at placement.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** The geometry subset of a zone needed for containment — a drawn GeoJSON polygon fence, or a
 * circle (center + radius). Structurally satisfied by StoreDeliveryZone and RiderActiveZone. */
export interface ZoneGeometry {
  geoPolygon?: string | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  radiusKm?: number | null;
}

/** Great-circle distance in km between two WGS-84 points (R = 6371 km). */
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Outer rings of a GeoJSON Polygon/MultiPolygon geometry string (holes ignored). */
export function parseGeoJsonRings(geoJson: string | null | undefined): LatLng[][] {
  if (!geoJson) return [];
  try {
    const g = JSON.parse(geoJson);
    const polys: unknown[] =
      g?.type === 'Polygon' ? [g.coordinates] : g?.type === 'MultiPolygon' ? g.coordinates : [];
    const rings: LatLng[][] = [];
    for (const poly of polys) {
      const outer = Array.isArray(poly) ? poly[0] : null;
      if (!Array.isArray(outer)) continue;
      // GeoJSON positions are [lng, lat].
      const ring = outer
        .map((c: number[]) => ({ lat: +c[1], lng: +c[0] }))
        .filter((p: LatLng) => isFinite(p.lat) && isFinite(p.lng));
      if (ring.length >= 3) rings.push(ring);
    }
    return rings;
  } catch {
    return [];
  }
}

/** Ray-casting point-in-polygon on raw lat/lng (planar approximation — fine at city scale). */
export function pointInRing(lat: number, lng: number, ring: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat, xi = ring[i].lng;
    const yj = ring[j].lat, xj = ring[j].lng;
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Whether a point is inside a zone — its polygon fence when drawn, else its circle. */
export function zoneContains(zone: ZoneGeometry, lat: number, lng: number): boolean {
  const rings = parseGeoJsonRings(zone.geoPolygon);
  if (rings.length > 0) return rings.some((r) => pointInRing(lat, lng, r));
  return (
    zone.centerLatitude != null &&
    zone.centerLongitude != null &&
    (zone.radiusKm ?? 0) > 0 &&
    haversineKm(lat, lng, zone.centerLatitude, zone.centerLongitude) <= (zone.radiusKm as number)
  );
}
